'use es6';

import SeamlessImmutable from 'seamless-immutable';
import CustomWidgetTags from 'ContentUtils/constants/CustomWidgetTags';
import * as client from 'DesignData/api/CustomWidgetBuffers';
import * as revisionsClient from 'DesignData/api/Revisions';
import { CUSTOM_WIDGET_BUFFER_DELETE, CUSTOM_WIDGET_BUFFER_DISCARD, CUSTOM_WIDGET_BUFFER_FETCH, CUSTOM_WIDGET_BUFFER_FETCH_BY_PATH, CUSTOM_WIDGET_BUFFER_PUBLISH, CUSTOM_WIDGET_BUFFER_SAVE, CUSTOM_WIDGET_FIELD_CREATE, CUSTOM_WIDGET_FIELD_CREATE_GROUP, CUSTOM_WIDGET_FIELD_CLONE, CUSTOM_WIDGET_FIELD_DELETE, CUSTOM_WIDGET_FIELD_EDIT, CUSTOM_WIDGET_FIELD_MOVE, CUSTOM_WIDGET_REVERT, CUSTOM_WIDGET_SOURCE_UPDATE, CUSTOM_WIDGET_BUFFER_VALIDATE, CUSTOM_WIDGET_BUFFER_UPGRADE, CUSTOM_WIDGET_BUFFER_FORCE_UPGRADE, CUSTOM_WIDGET_EDIT_UNDO, CUSTOM_WIDGET_EDIT_REDO, CUSTOM_WIDGET_BUFFER_ADD_ASSET, CUSTOM_WIDGET_BUFFER_UPDATE, CUSTOM_WIDGET_REVISION_FETCH, CUSTOM_WIDGET_GLOBAL_CONTENT_EDIT, CUSTOM_WIDGET_BUFFER_TRANSLATIONS_OPT_IN, CUSTOM_WIDGET_BUFFER_ADD_LANGUAGE, CUSTOM_WIDGET_BUFFER_DELETE_LANGUAGE, CUSTOM_WIDGET_BUFFER_UPDATE_LANGUAGE, CUSTOM_WIDGET_BUFFER_CHANGE_SOURCE_LANGUAGE, CUSTOM_WIDGET_BUFFER_UPDATE_MESSAGES, CUSTOM_WIDGET_BUFFER_UPDATE_TRANSLATION, CUSTOM_WIDGET_BUFFER_REPLACE_TRANSLATION, CUSTOM_WIDGET_BUFFER_REMOVE_SMART_RULES, CUSTOM_WIDGET_BUFFER_REMOVE_SMART_RULE, CUSTOM_WIDGET_BUFFER_ADD_SMART_RULE, CUSTOM_WIDGET_BUFFER_EDIT_SMART_RULES_ORDER, CUSTOM_WIDGET_BUFFER_EDIT_SMART_RULE, CUSTOM_WIDGET_BUFFER_MAKE_SMART, CUSTOM_WIDGET_BUFFERS_UPDATE_SMART_RULES, CUSTOM_WIDGET_BUFFER_SAVE_BY_PATH } from 'DesignData/actions/ActionTypes';
import { showAppNotification } from 'DesignData/actions/AppNotifications';
import { addStagedFile } from 'DesignData/actions/Staging';
import * as customWidgetBuffersSelectors from 'DesignData/selectors/customWidgetBuffers';
import { MAX_NESTING_DEPTH } from 'DesignData/constants/CustomWidgets';
import * as customWidgetFieldsHelper from 'DesignData/helpers/customWidgetFieldsHelper';
import getQuickFetchRequestPromise from 'DesignData/api/getQuickFetchRequestPromise';
import { NOT_MODIFIED } from 'DesignData/enums/HttpStatusCodes';
import { generateTranslationsFromFields } from 'DesignData/helpers/TranslationHelpers';
import { formatPathAsId, trimSlashesAndWhitespace } from 'DesignData/lib/text';
import { CUSTOM_WIDGET } from 'DesignData/enums/DesignAssetTypes';
import { transformIdsForNonMysqlResource } from 'DesignData/lib/hollow/migrate';
const getCustomWidgetBuffer = customWidgetBuffersSelectors.makeGetCustomWidgetBuffer();
const getCustomWidgetBufferPayload = customWidgetBuffersSelectors.makeGetCustomWidgetBufferPayload();
const getPastEditHistoryCount = customWidgetBuffersSelectors.makeGetPastEditHistoryCount();
const getFutureEditHistoryCount = customWidgetBuffersSelectors.makeGetFutureEditHistoryCount();
const getRequestStatus = customWidgetBuffersSelectors.makeGetRequestStatus();
const {
  getFields
} = customWidgetBuffersSelectors;
const getCustomWidgetBufferFields = customWidgetBuffersSelectors.makeGetCustomWidgetBufferFields();
const getTranslations = customWidgetBuffersSelectors.makeGetTranslations();
export function upgrade(customWidgetId, customWidgetData) {
  const {
    receive,
    error
  } = upgrade;
  return dispatch => {
    const upgradeData = Object.assign({
      isAvailableForNewContent: true
    }, customWidgetData);
    return client.upgrade(customWidgetId, upgradeData).then(customWidget => {
      dispatch(receive(customWidgetId, customWidget));
    }).catch(_error => {
      dispatch(error(customWidgetId, _error));
    });
  };
}

upgrade.receive = (customWidgetId, customWidget) => {
  return {
    type: CUSTOM_WIDGET_BUFFER_UPGRADE,
    customWidgetId,
    customWidget
  };
};

upgrade.error = (customWidgetId, error) => {
  return {
    type: CUSTOM_WIDGET_BUFFER_UPGRADE,
    customWidgetId,
    error
  };
};
/**
 * Creates a new customWidget object with changes to the object received
 * from server. Needed before dispatching.
 */


const createDispatchCustomWidgetFromFetched = (customWidgetId, customWidget, getState) => {
  //  TODO: remove all contentTags/tags processing when tags is deprecated

  /* Convert tags to contentTags, but if it was previously added, don't add a duplicate. Then, merge the lists. */
  const valueToKey = value => {
    return Object.keys(CustomWidgetTags).find(tag => CustomWidgetTags[tag] === value);
  }; // Filter out invalid contentTags (introduced from earlier dev)


  const filteredContentTags = (customWidget.contentTags || []).filter(tag => {
    const isCustomWidgetTagKey = Object.keys(CustomWidgetTags).includes(tag.name);
    const isUserTag = tag.source === 'USER';
    return !(isUserTag && !isCustomWidgetTagKey);
  }); //  Allow tags with same `name` but different `source` to both exist in the Set

  const existingContentTags = new Set();
  filteredContentTags.map(({
    name,
    source
  }) => `${name}:${source}`).forEach(el => existingContentTags.add(el));
  const filteredUserTags = (customWidget.tags || []).filter(tag => {
    const isCustomWidgetTagValue = Object.values(CustomWidgetTags).includes(tag);
    return isCustomWidgetTagValue;
  });
  const convertedTags = filteredUserTags.filter(tag => {
    const name = valueToKey(tag); //  `tags` are defined by the user, therefore source can only be `USER`

    return !existingContentTags.has(`${name}:USER`);
  }).map(tag => ({
    name: valueToKey(tag),
    source: 'USER'
  }));
  const mergedContentTags = [...filteredContentTags, ...convertedTags];
  return Object.assign({}, transformIdsForNonMysqlResource(CUSTOM_WIDGET, customWidgetId, customWidget, getState), {
    contentTags: mergedContentTags
  });
};

const fetchFactory = (type, transformId, getRequest) => {
  function __fetch(customWidgetId) {
    customWidgetId = transformId(customWidgetId);
    const {
      request,
      receive,
      error
    } = __fetch;
    return (dispatch, getState) => {
      dispatch(request(customWidgetId));
      const state = getState();
      const currentCustomWidget = getCustomWidgetBuffer(state, {
        customWidgetId
      });

      const successHandler = customWidget => {
        customWidget = createDispatchCustomWidgetFromFetched(customWidgetId, customWidget, getState);
        dispatch(receive(customWidget.id, customWidget));
      };

      const errorHandler = _error => {
        if (_error.status && _error.status !== NOT_MODIFIED) {
          dispatch(error(customWidgetId, _error));
        }
      };

      if (!currentCustomWidget) {
        const quickFetchPromise = getQuickFetchRequestPromise(`moduleId-${customWidgetId}`, () => client.fetch(customWidgetId, {}));

        if (quickFetchPromise) {
          return quickFetchPromise.then(successHandler).catch(errorHandler);
        }
      } // This query allows us to 304 if we already have latest from server.


      const query = currentCustomWidget && currentCustomWidget.updated ? {
        last_updated: currentCustomWidget.updated
      } : {};
      const req = getRequest(customWidgetId, currentCustomWidget, query);
      return req.then(successHandler).catch(errorHandler);
    };
  }

  __fetch.request = customWidgetId => ({
    type,
    customWidgetId
  });

  __fetch.receive = (customWidgetId, customWidget) => ({
    type,
    customWidgetId,
    customWidget
  });

  __fetch.error = (customWidgetId, error) => ({
    type,
    customWidgetId,
    error
  });

  return __fetch;
};

export const fetch = fetchFactory(CUSTOM_WIDGET_BUFFER_FETCH, id => id, (customWidgetId, currentCustomWidget, query) => {
  const path = currentCustomWidget && currentCustomWidget.path;
  return path && currentCustomWidget.staged ? client.fetchByPath(path, query) : client.fetch(customWidgetId, query);
});
export const fetchByPath = fetchFactory(CUSTOM_WIDGET_BUFFER_FETCH_BY_PATH, formatPathAsId, (customWidgetId, currentCustomWidget, query) => {
  return client.fetchByPath(trimSlashesAndWhitespace(customWidgetId), query);
});
export function save(customWidgetId) {
  const {
    request,
    receive,
    error
  } = save;
  return (dispatch, getState) => {
    const currentState = getState();
    const {
      isSaving,
      isPublishing
    } = getRequestStatus(currentState, {
      customWidgetId
    });

    if (isSaving || isPublishing) {
      return null;
    }

    dispatch(request(customWidgetId));
    const payload = getCustomWidgetBufferPayload(currentState, {
      customWidgetId
    });
    return client.save(customWidgetId, payload).then(customWidget => {
      dispatch(receive(customWidgetId, customWidget, payload));
    }).catch(_error => {
      const {
        css,
        js
      } = getCustomWidgetBuffer(currentState, {
        customWidgetId
      });
      dispatch(error(customWidgetId, _error, {
        css,
        js
      }));
    });
  };
}

save.request = customWidgetId => {
  return {
    type: CUSTOM_WIDGET_BUFFER_SAVE,
    customWidgetId
  };
};

save.receive = (customWidgetId, customWidget, customWidgetBuffer) => {
  return {
    type: CUSTOM_WIDGET_BUFFER_SAVE,
    customWidgetId,
    customWidget,
    customWidgetBuffer
  };
};

save.error = (customWidgetId, error, customWidgetData) => {
  return {
    type: CUSTOM_WIDGET_BUFFER_SAVE,
    customWidgetId,
    error,
    customWidgetData
  };
};

export function saveByPath(customWidgetId) {
  const {
    request,
    receive,
    error
  } = saveByPath;
  return (dispatch, getState) => {
    const currentState = getState();
    const {
      isSaving,
      isPublishing
    } = getRequestStatus(currentState, {
      customWidgetId
    });

    if (isSaving || isPublishing) {
      return null;
    }

    const payload = getCustomWidgetBufferPayload(currentState, {
      customWidgetId
    });
    dispatch(request(customWidgetId, payload));
    return client.saveByPath(payload.path, payload).then(customWidget => {
      dispatch(receive(customWidgetId, customWidget, payload));
      dispatch(addStagedFile(payload, customWidget));
    }).catch(_error => {
      const {
        css,
        js
      } = getCustomWidgetBuffer(currentState, {
        customWidgetId
      });
      dispatch(error(customWidgetId, _error, {
        css,
        js
      }));
    });
  };
}

saveByPath.request = (customWidgetId, customWidgetBuffer) => {
  return {
    type: CUSTOM_WIDGET_BUFFER_SAVE_BY_PATH,
    customWidgetId,
    customWidgetBuffer
  };
};

saveByPath.receive = (customWidgetId, customWidget, customWidgetBuffer) => {
  return {
    type: CUSTOM_WIDGET_BUFFER_SAVE_BY_PATH,
    customWidgetId,
    customWidget,
    customWidgetBuffer
  };
};

saveByPath.error = (customWidgetId, error, customWidgetData) => {
  return {
    type: CUSTOM_WIDGET_BUFFER_SAVE_BY_PATH,
    customWidgetId,
    error,
    customWidgetData
  };
};

export function forceUpgrade(customWidgetId, customWidgetData = {}) {
  const {
    request,
    receive,
    error
  } = forceUpgrade;
  return (dispatch, getState) => {
    dispatch(request(customWidgetId));
    const data = Object.assign({
      schemaVersion: 2,
      isAvailableForNewContent: true
    }, customWidgetData);
    return client.save(customWidgetId, data).then(customWidget => {
      dispatch(receive(customWidgetId, customWidget));
    }).catch(_error => {
      const customWidget = getCustomWidgetBuffer(getState(), {
        customWidgetId
      });
      const newCustomWidget = customWidget ? customWidget.merge(data) : null;
      dispatch(error(customWidgetId, _error, newCustomWidget));
    });
  };
}

forceUpgrade.request = customWidgetId => {
  return {
    type: CUSTOM_WIDGET_BUFFER_FORCE_UPGRADE,
    customWidgetId
  };
};

forceUpgrade.receive = (customWidgetId, customWidget) => {
  return {
    type: CUSTOM_WIDGET_BUFFER_FORCE_UPGRADE,
    customWidgetId,
    customWidget
  };
};

forceUpgrade.error = (customWidgetId, error, customWidget) => {
  return {
    type: CUSTOM_WIDGET_BUFFER_FORCE_UPGRADE,
    customWidgetId,
    error,
    customWidget
  };
};

export function publish(customWidgetId) {
  const {
    request,
    receive,
    error
  } = publish;
  return (dispatch, getState) => {
    dispatch(request(customWidgetId));
    const payload = getCustomWidgetBufferPayload(getState(), {
      customWidgetId
    });
    return client.publish(customWidgetId, payload).then(customWidget => {
      dispatch(receive(customWidgetId, customWidget));
    }).catch(_error => {
      dispatch(error(customWidgetId, _error));
    });
  };
}

publish.request = customWidgetId => {
  return {
    type: CUSTOM_WIDGET_BUFFER_PUBLISH,
    customWidgetId
  };
};

publish.receive = (customWidgetId, customWidget) => {
  return {
    type: CUSTOM_WIDGET_BUFFER_PUBLISH,
    customWidgetId,
    customWidget
  };
};

publish.error = (customWidgetId, error) => {
  return {
    type: CUSTOM_WIDGET_BUFFER_PUBLISH,
    customWidgetId,
    error
  };
};

export function createField(customWidgetId, field, parentFieldId) {
  return {
    type: CUSTOM_WIDGET_FIELD_CREATE,
    customWidgetId,
    field,
    parentFieldId
  };
}
export function createFieldGroup(customWidgetId, fieldGroup, children = []) {
  return {
    type: CUSTOM_WIDGET_FIELD_CREATE_GROUP,
    customWidgetId,
    fieldGroup,
    children
  };
}
export function deleteField(customWidgetId, fieldId) {
  return {
    type: CUSTOM_WIDGET_FIELD_DELETE,
    customWidgetId,
    fieldId
  };
}
export function editField(customWidgetId, field) {
  return {
    type: CUSTOM_WIDGET_FIELD_EDIT,
    customWidgetId,
    field
  };
}
export function cloneField(customWidgetId, fieldId) {
  return (dispatch, getState) => {
    const fieldsState = getFields(getState(), {
      customWidgetId
    });
    const {
      clonedField,
      parentFieldId,
      index
    } = customWidgetFieldsHelper.cloneField(fieldsState, fieldId);
    dispatch({
      type: CUSTOM_WIDGET_FIELD_CLONE,
      customWidgetId,
      clonedField,
      parentFieldId,
      index
    });
  };
}
export function editGlobalContent(customWidgetId, globalContent) {
  return {
    type: CUSTOM_WIDGET_GLOBAL_CONTENT_EDIT,
    customWidgetId,
    globalContent
  };
}
/**
 * Finder sync middleware action
 * @param {(number, number[])} a single widget id or array of widget ids to delete.
 */

export function deleteCustomWidgets(widgetsToDelete) {
  return {
    type: CUSTOM_WIDGET_BUFFER_DELETE,
    customWidgetId: widgetsToDelete
  };
}
export function moveCustomWidgetField(customWidgetId, fieldId, replaceFieldId, insertType) {
  const {
    move
  } = moveCustomWidgetField;
  return (dispatch, getState) => {
    const {
      getCustomWidgetBufferFieldsMap
    } = customWidgetBuffersSelectors;
    const fieldsMap = getCustomWidgetBufferFieldsMap(getState(), {
      customWidgetId
    });

    if (customWidgetFieldsHelper.isTooDeepOnDrag(fieldsMap, fieldId, replaceFieldId, insertType)) {
      dispatch(showAppNotification({
        notificationKey: 'customWidgetGroupsTooDeep',
        type: 'warning',
        messageProps: {
          maxNestingDepth: MAX_NESTING_DEPTH
        }
      }));
    } else {
      dispatch(move(customWidgetId, fieldId, replaceFieldId, insertType));
    }
  };
}

moveCustomWidgetField.move = (customWidgetId, fieldId, replaceFieldId, insertType) => {
  return {
    type: CUSTOM_WIDGET_FIELD_MOVE,
    customWidgetId,
    fieldId,
    replaceFieldId,
    insertType
  };
};

export function fetchRevision(customWidgetId, revisionId) {
  const {
    request,
    receive,
    error
  } = fetchRevision;
  return (dispatch, getState) => {
    const state = getState();
    const customWidgetRevision = getCustomWidgetBuffer(state, {
      customWidgetId,
      revisionId
    });

    if (customWidgetRevision) {
      /* No need to download */
      return;
    }

    dispatch(request(customWidgetId));
    revisionsClient.fetchCustomWidgetRevision(customWidgetId, revisionId).then(customWidget => {
      dispatch(receive(customWidgetId, revisionId, customWidget));
    }).catch(__error => {
      dispatch(error(customWidgetId, revisionId, __error));
    });
  };
}

fetchRevision.request = (customWidgetId, revisionId) => {
  return {
    type: CUSTOM_WIDGET_REVISION_FETCH,
    customWidgetId,
    revisionId
  };
};

fetchRevision.receive = (customWidgetId, revisionId, customWidget) => {
  return {
    type: CUSTOM_WIDGET_REVISION_FETCH,
    customWidgetId,
    revisionId,
    customWidget
  };
};

fetchRevision.error = (customWidgetId, revisionId, err) => {
  return {
    type: CUSTOM_WIDGET_REVISION_FETCH,
    customWidgetId,
    revisionId,
    error: err
  };
};

export function revertCustomWidget(revision) {
  const customWidgetId = revision.object.id;
  const {
    receive,
    error
  } = revertCustomWidget;
  return dispatch => {
    return revisionsClient.revertCustomWidget(customWidgetId, revision.id, true).then(customWidget => {
      dispatch(receive(customWidgetId, customWidget));
    }).catch(_error => {
      dispatch(error(customWidgetId, _error));
    });
  };
}

revertCustomWidget.receive = (customWidgetId, customWidget) => {
  return {
    type: CUSTOM_WIDGET_REVERT,
    customWidgetId,
    customWidget
  };
};

revertCustomWidget.error = (customWidgetId, error) => {
  return {
    type: CUSTOM_WIDGET_REVERT,
    customWidgetId,
    error
  };
};

export function updateCustomWidgetSource(customWidgetId, sourceData) {
  return Object.assign({
    type: CUSTOM_WIDGET_SOURCE_UPDATE,
    customWidgetId
  }, sourceData);
}
export function discardBuffer(customWidgetId) {
  const {
    receive,
    error
  } = discardBuffer;
  return dispatch => {
    return client.discardBuffer(customWidgetId).then(customWidget => {
      dispatch(receive(customWidgetId, customWidget));
    }).catch(_error => {
      dispatch(error(customWidgetId, _error));
    });
  };
}

discardBuffer.receive = (customWidgetId, customWidget) => {
  return {
    type: CUSTOM_WIDGET_BUFFER_DISCARD,
    customWidgetId,
    customWidget
  };
};

discardBuffer.error = (customWidgetId, error) => {
  return {
    type: CUSTOM_WIDGET_BUFFER_DISCARD,
    customWidgetId,
    error
  };
};

export function validate(customWidgetId) {
  const {
    request,
    receive,
    error
  } = validate;
  return (dispatch, getState) => {
    let state = getState();
    const isSaving = state.customWidgetBuffers.isSavingById[customWidgetId];

    if (isSaving) {
      return null;
    }

    dispatch(request(customWidgetId));
    return client.validate(customWidgetId).then(warnings => {
      dispatch(receive(customWidgetId, warnings));
    }).catch(_error => {
      state = getState();
      const {
        css,
        js
      } = getCustomWidgetBuffer(state, {
        customWidgetId
      });
      dispatch(error(customWidgetId, _error, {
        css,
        js
      }));
    });
  };
}

validate.request = customWidgetId => {
  return {
    type: CUSTOM_WIDGET_BUFFER_VALIDATE,
    customWidgetId
  };
};

validate.receive = (customWidgetId, warnings) => {
  return {
    type: CUSTOM_WIDGET_BUFFER_VALIDATE,
    customWidgetId,
    isValid: true,
    warnings
  };
};

validate.error = (customWidgetId, error, customWidgetData) => {
  return {
    type: CUSTOM_WIDGET_BUFFER_VALIDATE,
    customWidgetId,
    error,
    customWidgetData
  };
};

export function undoEdit(customWidgetId) {
  return (dispatch, getState) => {
    const pastHistoryCount = getPastEditHistoryCount(getState(), {
      customWidgetId
    });

    if (pastHistoryCount) {
      dispatch({
        type: CUSTOM_WIDGET_EDIT_UNDO,
        customWidgetId
      });
    }
  };
}
export function redoEdit(customWidgetId) {
  return (dispatch, getState) => {
    const futureHistoryCount = getFutureEditHistoryCount(getState(), {
      customWidgetId
    });

    if (futureHistoryCount) {
      dispatch({
        type: CUSTOM_WIDGET_EDIT_REDO,
        customWidgetId
      });
    }
  };
}
export function addAsset(customWidgetId, assetsType, asset) {
  return {
    type: CUSTOM_WIDGET_BUFFER_ADD_ASSET,
    customWidgetId,
    assetsType,
    asset
  };
}
export function update(customWidgetId, changes) {
  return {
    type: CUSTOM_WIDGET_BUFFER_UPDATE,
    customWidgetId,
    changes
  };
}
export function optInTranslations(customWidgetId, customWidgetLabel, langCode) {
  return (dispatch, getState) => {
    const fields = getCustomWidgetBufferFields(getState(), {
      customWidgetId
    });
    const translations = generateTranslationsFromFields(customWidgetLabel, fields, true);
    dispatch({
      type: CUSTOM_WIDGET_BUFFER_TRANSLATIONS_OPT_IN,
      customWidgetId,
      langCode,
      translations
    });
  };
}
export function addLanguage(customWidgetId, customWidgetLabel, langCode, includeDescription = false) {
  return (dispatch, getState) => {
    const fields = getCustomWidgetBufferFields(getState(), {
      customWidgetId
    });
    const translations = generateTranslationsFromFields(customWidgetLabel, fields, includeDescription);
    dispatch({
      type: CUSTOM_WIDGET_BUFFER_ADD_LANGUAGE,
      customWidgetId,
      langCode,
      translations
    });
  };
}
export function deleteLanguage(customWidgetId, langCode) {
  return {
    type: CUSTOM_WIDGET_BUFFER_DELETE_LANGUAGE,
    customWidgetId,
    langCode
  };
}
export function updateLanguage(customWidgetId, langCode, translations) {
  return {
    type: CUSTOM_WIDGET_BUFFER_UPDATE_LANGUAGE,
    customWidgetId,
    langCode,
    translations
  };
}
export function changeMasterLanguage(customWidgetId, customWidgetLabel, langCode) {
  return (dispatch, getState) => {
    const fields = getCustomWidgetBufferFields(getState(), {
      customWidgetId
    });
    const translations = generateTranslationsFromFields(customWidgetLabel, fields, true);
    dispatch({
      type: CUSTOM_WIDGET_BUFFER_CHANGE_SOURCE_LANGUAGE,
      customWidgetId,
      langCode,
      translations
    });
  };
}
export function updateMessages(customWidgetId, messages) {
  return {
    type: CUSTOM_WIDGET_BUFFER_UPDATE_MESSAGES,
    customWidgetId,
    messages
  };
}
export function updateTranslation(customWidgetId, langCode, messages) {
  return {
    type: CUSTOM_WIDGET_BUFFER_UPDATE_TRANSLATION,
    customWidgetId,
    langCode,
    messages
  };
}
export function regenerateTranslations(customWidgetId, customWidgetLabel, langCode) {
  return (dispatch, getState) => {
    const state = getState();
    const currentTranslations = getTranslations(state, {
      customWidgetId
    });
    const isSourceLanguage = langCode === currentTranslations.masterLanguage;
    const currentMessages = currentTranslations.messages[langCode] || {};
    const fields = getCustomWidgetBufferFields(state, {
      customWidgetId
    });
    const translations = SeamlessImmutable(generateTranslationsFromFields(customWidgetLabel, fields, isSourceLanguage));
    const updatedTranslations = Object.keys(translations).reduce((hash, key) => {
      const description = currentMessages[key] && currentMessages[key].description;
      const message = currentMessages[key] && currentMessages[key].message;
      let __hash = hash;
      if (description) __hash = __hash.setIn([key, 'description'], description);
      if (!isSourceLanguage) __hash = __hash.setIn([key, 'message'], message || translations[key].message || '');
      return __hash;
    }, translations);
    dispatch({
      type: CUSTOM_WIDGET_BUFFER_REPLACE_TRANSLATION,
      customWidgetId,
      langCode,
      messages: updatedTranslations
    });
  };
}
/*
 * Smart content actions
 *
 */

/**
 * Remove a custom widget's smart rules
 *
 * @param {Number} customWidgetId Id of custom widget.
 */

export function removeSmartRules(customWidgetId) {
  return {
    type: CUSTOM_WIDGET_BUFFER_REMOVE_SMART_RULES,
    customWidgetId
  };
}
/**
 * Add an individual smart rule for a custom widget.
 *
 * @param {Number} customWidgetId Id of custom widget.
 * @param {Object} smartObject    Object returned from smart rules manager
 */

export function addSmartRule(customWidgetId, smartObject) {
  return {
    type: CUSTOM_WIDGET_BUFFER_ADD_SMART_RULE,
    customWidgetId,
    smartObject
  };
}
/**
 * Edit an individual smart rule for a custom widget.
 *
 * @param {Number} customWidgetId Id of custom widget.
 * @param {Number} smartRuleId    Id of smart rule to edit.
 * @param {Object} edits          Edits to be made to smart rule.
 */

export function editSmartRule(customWidgetId, smartRuleId, edits = {}) {
  return {
    type: CUSTOM_WIDGET_BUFFER_EDIT_SMART_RULE,
    customWidgetId,
    smartRuleId,
    edits
  };
}
/**
 * Remove an individual smart rule for a custom widget.
 *
 * @param {Number} customWidgetId Id of custom widget.
 * @param {Number} smartRuleId    Id of smart rule to remove.
 */

export function removeSmartRule(customWidgetId, smartRuleId) {
  return {
    type: CUSTOM_WIDGET_BUFFER_REMOVE_SMART_RULE,
    customWidgetId,
    smartRuleId
  };
}
/**
 * Edit smart rules order for a custom widget.
 *
 * @param {Number} customWidgetId Id of custom widget.
 * @param {Array.<Object>} smartObjects Array of smart rule objects.
 */

export function editSmartRulesOrder(customWidgetId, smartObjects) {
  return {
    type: CUSTOM_WIDGET_BUFFER_EDIT_SMART_RULES_ORDER,
    customWidgetId,
    smartObjects
  };
}
/**
 * Make a custom widget smart.
 *
 * @param {Number} customWidgetId Id of custom widget.
 * @param {Object} smartObjectData Contains smart type, initial smart rule, definition id.
 */

export function makeSmart(customWidgetId, smartObjectData) {
  return {
    type: CUSTOM_WIDGET_BUFFER_MAKE_SMART,
    customWidgetId,
    smartObjectData
  };
}
/**
 * Update a custom widget's smart rules and definition
 *
 * @param {Number} customWidgetId Id of custom widget
 * @param {Array.<Object>} smartObjects List of new smart rules
 * @param {Object} smartMetaData Object containing definition ID and smart type
 */

export function updateSmartRules(customWidgetId, smartObjects, smartMetaData) {
  return {
    type: CUSTOM_WIDGET_BUFFERS_UPDATE_SMART_RULES,
    customWidgetId,
    smartObjects,
    smartMetaData
  };
}